import cn from 'clsx'
import { ComponentProps, FC, RefObject } from 'react'

import { Button, Skeleton } from '@/components/ui'

import Stop from '@/assets/icons/stop.svg'

import { ITranscriptions } from '@/types/task.interface'

import SelectedSegmentFolderMenu from './SelectedSegmentFolderMenu'
import TranscriptionBlock from './TranscriptionBlock'

interface ILiveTranslationColumn {
	isCollapsed: boolean
	isLoading: boolean
	showStopButton: boolean
	onStop: () => void
	onExpandOriginal: () => void
	containerRef: RefObject<HTMLDivElement>
	onScroll: () => void
	transcriptions: ITranscriptions | null
	textProps: Pick<
		ComponentProps<typeof TranscriptionBlock>,
		| 'refCollector'
		| 'onRendered'
		| 'onSeek'
		| 'folders'
		| 'isTranscribingStopped'
		| 'taskId'
		| 'keywordMatches'
	>
	segmentMenuProps: ComponentProps<typeof SelectedSegmentFolderMenu> | null
}

const LiveTranslationColumn: FC<ILiveTranslationColumn> = ({
	isCollapsed,
	isLoading,
	showStopButton,
	onStop,
	onExpandOriginal,
	containerRef,
	onScroll,
	transcriptions,
	textProps,
	segmentMenuProps
}) => {
	return (
		<section className='flex-1 relative bg-gray-100 overflow-hidden'>
			{showStopButton && (
				<Button
					className='absolute top-4 right-6 bg-white rounded-[4px] !px-2 !py-[6px]'
					classNameText='flex items-center gap-2 !font-[500] !text-[12px] !leading-[16px] text-black'
					onClick={onStop}
				>
					<Stop />
					<span>Stop transcribing</span>
				</Button>
			)}
			{isCollapsed && (
				<Button
					onClick={onExpandOriginal}
					className='absolute top-5 left-4 z-10 bg-black/20 rounded-full w-fit p-2 rotate-180 hover:bg-black/15'
					icon={{
						name: 'back',
						size: 12,
						color: '#222222'
					}}
				/>
			)}

			<div
				ref={containerRef}
				onScroll={onScroll}
				className={cn(
					'relative transition-all ease-in-out flex flex-col overflow-y-auto h-full px-10 py-5 pt-10',
					isCollapsed ? 'items-center mx-auto' : 'w-auto'
				)}
			>
				{isLoading ? (
					<Skeleton className='mb-2 h-[26px]' />
				) : (
					<>
						<TranscriptionBlock
							{...textProps}
							isCollapsed={isCollapsed}
							items={transcriptions.transcriptions.translation}
							highlightable
							translation
						/>

						{segmentMenuProps && (
							<SelectedSegmentFolderMenu {...segmentMenuProps} />
						)}
					</>
				)}
			</div>
		</section>
	)
}

export default LiveTranslationColumn
